import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { splitItem } from "../../vendor/src/features/remotion-editor/editor/state/actions/split-item";
import { resolveGroupGeometry } from "../../vendor/src/features/remotion-editor/editor/items/group/group-geometry";
import { editableTree, itemOwner } from "./group-split";
import { mergeTimeRanges, removeTimelineTime } from "./timeline-ripple";
import { insertTimelineTime } from "./timeline-insert";

export type PlacementResult =
  | { ok: true; state: EditorState }
  | {
      ok: false;
      state: EditorState;
      reason:
        | "invalid-position"
        | "locked"
        | "missing-item"
        | "target-occupied"
        | "invalid-tree";
    };
const staticTypes = new Set(["image", "text", "solid", "blur", "pdf", "ppt"]);

/** Exact start input and magnetic insert/reorder share this operation. No source clock is retimed. */
export function placeTimelineSelection(
  state: EditorState,
  ids: readonly string[],
  primaryId: string,
  frame: number,
  options: { intent?: "nudge" } = {},
): PlacementResult {
  const fail = (
    reason: Extract<PlacementResult, { ok: false }>["reason"],
  ): PlacementResult => ({ ok: false, state, reason });
  if (!Number.isSafeInteger(frame) || frame < 0)
    return fail("invalid-position");
  const primary = state.undoableState.items[primaryId],
    primaryOwner = itemOwner(state, primaryId);
  if (!primary || !primaryOwner) return fail("missing-item");
  if (primaryOwner.track.locked) return fail("locked");
  const delta = frame - primary.from;
  if (!delta) return { ok: true, state };
  const selected = [
    ...new Set(ids.includes(primaryId) ? ids : [primaryId]),
  ].filter((id) => {
    const owner = itemOwner(state, id);
    return owner && !owner.track.locked && state.undoableState.items[id];
  });
  if (selected.some((id) => !editableTree(state, id)))
    return fail("invalid-tree");
  // A selected ancestor owns its descendants exactly once.
  const roots = selected.filter((id) => {
    let owner = itemOwner(state, id);
    while (owner?.parentId) {
      if (selected.includes(owner.parentId)) return false;
      owner = itemOwner(state, owner.parentId);
    }
    return true;
  });
  const mains = roots.filter((id) => {
    const owner = itemOwner(state, id);
    return (
      owner &&
      !owner.parentId &&
      owner.track.metadata?.isMainTrack &&
      owner.track.metadata.type === "video"
    );
  });
  const sync =
    state.isLinkingEnabled &&
    !primaryOwner.parentId &&
    primaryOwner.track.metadata?.isMainTrack === true &&
    primaryOwner.track.metadata.type === "video" &&
    mains.length > 0;
  const anchors = sync ? mains : roots;
  let ranges: ReturnType<typeof mergeTimeRanges>;
  try {
    ranges = mergeTimeRanges(
      anchors.map((id) => {
        const item = state.undoableState.items[id]!;
        return { start: item.from, end: item.from + item.durationInFrames };
      }),
    );
  } catch {
    return fail("invalid-position");
  }
  const mainFrom = Math.min(
    ...anchors.map((id) => state.undoableState.items[id]!.from),
  );
  const destination = mainFrom + delta;
  if (destination < 0 || !Number.isSafeInteger(destination))
    return fail("invalid-position");
  const magnetic =
    options.intent !== "nudge" &&
    primaryOwner.track.magnetic &&
    !primaryOwner.parentId;
  const scope = new Set(
    sync
      ? state.undoableState.tracks
          .filter((track) => !track.locked)
          .map((track) => track.id)
      : anchors.map((id) => itemOwner(state, id)!.track.id),
  );
  if (magnetic && !sync && scope.size > 1) {
    let current = { ...state, isLinkingEnabled: false };
    for (const trackId of scope) {
      const local = anchors.filter(
        (id) => itemOwner(state, id)?.track.id === trackId,
      );
      if (!local.length) continue;
      const result = placeTimelineSelection(
        current,
        local,
        local[0]!,
        state.undoableState.items[local[0]!]!.from + delta,
      );
      if (!result.ok) return fail(result.reason);
      current = result.state;
    }
    return {
      ok: true,
      state: {
        ...current,
        isLinkingEnabled: state.isLinkingEnabled,
        selectedItems: anchors.filter((id) => current.undoableState.items[id]),
      },
    };
  }
  const originalEnd = Math.max(
    0,
    ...state.undoableState.tracks.flatMap((track) =>
      track.items.map((id) => {
        const item = state.undoableState.items[id];
        return item ? item.from + item.durationInFrames : 0;
      }),
    ),
  );
  const protectedIds = new Set(
    state.undoableState.tracks.flatMap((track) =>
      track.items.filter((id) => {
        const item = state.undoableState.items[id];
        return (
          item &&
          staticTypes.has(item.type) &&
          item.from === 0 &&
          item.durationInFrames >= originalEnd &&
          ((item.timelineSourceItemId ?? id).startsWith("waka-story-root-") ||
            track.metadata?.trackType === "brand-template")
        );
      }),
    ),
  );
  let prepared = state;
  if (sync) {
    // Extract only the selected scene windows from long narration/caption/media roots.
    for (const boundary of [
      ...new Set(ranges.flatMap((range) => [range.start, range.end])),
    ].sort((a, b) => a - b)) {
      for (const track of prepared.undoableState.tracks) {
        if (!scope.has(track.id)) continue;
        for (const id of [...track.items]) {
          const item = prepared.undoableState.items[id];
          if (!item) return fail("missing-item");
          if (
            protectedIds.has(id) ||
            item.from >= boundary ||
            item.from + item.durationInFrames <= boundary
          )
            continue;
          const next = splitItem({
            state: prepared,
            idToSplit: id,
            framePosition: boundary,
          });
          if (next === prepared) return fail("invalid-tree");
          prepared = next;
        }
      }
    }
  }
  const payload = sync
    ? prepared.undoableState.tracks.flatMap((track) =>
        scope.has(track.id)
          ? track.items.filter((id) => {
              const item = prepared.undoableState.items[id]!;
              return (
                !protectedIds.has(id) &&
                ranges.some(
                  (range) =>
                    item.from >= range.start &&
                    item.from + item.durationInFrames <= range.end,
                )
              );
            })
          : [],
      )
    : anchors;
  if (payload.some((id) => !editableTree(prepared, id)))
    return fail("invalid-tree");
  const payloadSet = new Set(payload);
  const shiftTree = (
    items: EditorState["undoableState"]["items"],
    id: string,
    offset: number,
    seen: Set<string>,
  ) => {
    if (seen.has(id)) return;
    seen.add(id);
    const item = items[id];
    if (!item) return;
    if (item.type === "group")
      for (const child of item.children) shiftTree(items, child, offset, seen);
    items[id] = { ...item, from: item.from + offset };
  };
  if (!magnetic) {
    if (
      payload.some((id) => prepared.undoableState.items[id]!.from + delta < 0)
    )
      return fail("invalid-position");
    const items = { ...prepared.undoableState.items },
      moved = new Set<string>();
    payload.forEach((id) => shiftTree(items, id, delta, moved));
    for (const track of prepared.undoableState.tracks) {
      for (const id of track.items.filter((id) => payloadSet.has(id))) {
        const item = items[id]!;
        if (
          track.items.some((other) => {
            const neighbor = items[other];
            return (
              !payloadSet.has(other) &&
              neighbor &&
              item.from < neighbor.from + neighbor.durationInFrames &&
              neighbor.from < item.from + item.durationInFrames
            );
          })
        )
          return fail("target-occupied");
      }
    }
    for (const id of payload) {
      let owner = itemOwner(prepared, id);
      while (owner?.parentId) {
        const group = items[owner.parentId];
        if (group?.type === "group")
          items[group.id] = {
            ...group,
            ...resolveGroupGeometry(group, items),
          };
        owner = itemOwner(prepared, owner.parentId);
      }
    }
    const transitions = Object.fromEntries(
      Object.entries(prepared.undoableState.transitions)
        .filter(
          ([, t]) => moved.has(t.sourceItemId) === moved.has(t.targetItemId),
        )
        .map(([id, t]) => [
          id,
          moved.has(t.sourceItemId) ? { ...t, from: t.from + delta } : t,
        ]),
    );
    return {
      ok: true,
      state: {
        ...prepared,
        timelineTimeEditApplied: true,
        selectedItems: anchors.filter((id) => items[id]),
        undoableState: { ...prepared.undoableState, items, transitions },
      },
    };
  }
  // Reserve held IDs in items while removing their track references so split IDs cannot collide.
  const held = new Set([...payload, ...protectedIds]);
  let remainder = {
    ...prepared,
    undoableState: {
      ...prepared.undoableState,
      tracks: prepared.undoableState.tracks.map((track) => ({
        ...track,
        items: track.items.filter((id) => !held.has(id)),
      })),
    },
  };
  const removed = removeTimelineTime(remainder, [...scope], ranges);
  if (!removed.ok) return fail("invalid-tree");
  remainder = removed.state;
  const duration = ranges.reduce(
    (sum, range) => sum + range.end - range.start,
    0,
  );
  const inserted = insertTimelineTime(
    remainder,
    [...scope],
    destination,
    duration,
    [],
    { crossingVisuals: "split" },
  );
  if (!inserted.ok) return fail("invalid-tree");
  const items = { ...inserted.state.undoableState.items },
    moved = new Set<string>();
  for (const id of payload) {
    const item = prepared.undoableState.items[id]!;
    let packed = 0;
    for (const range of ranges) {
      if (
        item.from >= range.start &&
        item.from + item.durationInFrames <= range.end
      ) {
        shiftTree(items, id, destination + packed - range.start, moved);
        break;
      }
      packed += range.end - range.start;
    }
  }
  const tracks = inserted.state.undoableState.tracks.map((track) => ({
    ...track,
    items: [
      ...track.items,
      ...prepared.undoableState.tracks
        .find((t) => t.id === track.id)!
        .items.filter((id) => held.has(id)),
    ].sort((a, b) => items[a]!.from - items[b]!.from),
  }));
  const end = Math.max(
    0,
    ...tracks.flatMap((track) =>
      track.items
        .filter((id) => !protectedIds.has(id))
        .map((id) => items[id]!.from + items[id]!.durationInFrames),
    ),
  );
  for (const id of protectedIds) {
    const original = state.undoableState.items[id]!;
    items[id] =
      end > originalEnd
        ? {
            ...original,
            durationInFrames: original.durationInFrames + end - originalEnd,
          }
        : original;
  }
  const transitions = { ...inserted.state.undoableState.transitions };
  for (const [id, t] of Object.entries(prepared.undoableState.transitions)) {
    if (moved.has(t.sourceItemId) && moved.has(t.targetItemId))
      transitions[id] = {
        ...t,
        from:
          t.from +
          (items[t.sourceItemId]!.from -
            prepared.undoableState.items[t.sourceItemId]!.from),
      };
    else if (moved.has(t.sourceItemId) || moved.has(t.targetItemId))
      delete transitions[id];
  }
  return {
    ok: true,
    state: {
      ...inserted.state,
      timelineTimeEditApplied: true,
      selectedItems: anchors.filter((id) => items[id]),
      undoableState: {
        ...inserted.state.undoableState,
        items,
        tracks,
        transitions,
      },
    },
  };
}
